import React, { Component } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { withAuth, IAuthProps } from "../../../laco-common/auth/AuthContext";
import { DynamicForm } from "../../../forms/DynamicForm";
import { DynamicFormValidation, DynamicFormField } from "../../../laco-common/base/form-validation/dynamic";
import NotificationsService from "../../../laco-common/services/notifications";
import { UserImageIcon } from "../ui/UserImageIcon";
import { FormValidation } from "../../../laco-common/base/form-validation";
import { IUserProfileData } from "../../../laco-common/base/models/user";
import { formatPhoneNumber } from "../../../utils";
import { UIStyledTooltip } from "../../ui/UIComponents";
import MembershipDetails from "../../streams/MembershipDetails";
import "./UpdateProfile.scss";

type UpdateProfileState = {
	form: DynamicFormValidation;
	sendingPasswordReset: boolean;
	sendingEmailVerification: boolean;
};

export type ProfileDataFields = keyof Partial<IUserProfileData | { password }>;

export const getBaseProfileFields = (
	omitAsterik: boolean,
	profile: Partial<IUserProfileData> = {}
): { [key in ProfileDataFields]: DynamicFormField } => {
	return {
		first_name: {
			order: 1,
			label: "First Name",
			autoComplete: "first-name",
			mandatory: true,
			valid: true,
			value: profile.first_name || "",
			omitAsterik,
		},
		last_name: {
			order: 2,
			label: "Last Name",
			autoComplete: "last-name",
			mandatory: true,
			valid: true,
			value: profile.last_name || "",
			omitAsterik,
		},
		primary_email: {
			order: 5,
			label: "Email / Username",
			valid: true,
			email: true,
			mandatory: true,
			value: profile.primary_email || "",
			omitAsterik,
			// postLabelContent: this.getEditLabelContent(),
		},
		password: {
			order: 6,
			label: "Password",
			valid: false,
			fieldType: "password" as any,
			autoComplete: "password",
			placeholder: "Enter your password",
			value: "",
			mandatory: true,
			omitAsterik,
		},
		// secondary_email: {
		// 	order: 7,
		// 	label: "Secondary Email",
		// 	valid: true,
		// 	mandatory: false,
		// 	email: true,
		// 	value: profile.secondary_email || "",
		// },
		// mobile_number: {
		// 	order: 7,
		// 	label: "Mobile",
		// 	valid: true,
		// 	disabled: true,
		// 	value: this.props.authContext.userPayload.profile.mobile_number || "",
		// },
		address: {
			order: 10,
			label: "Address",
			autoComplete: "address",
			valid: true,
			mandatory: true,
			multiline: true,
			value: profile.address || "",
			fieldType: "address",
		},
		// employeeId: {
		// 	order: 8,
		// 	label: "Employee Id",
		// 	valid: true,
		// 	disabled: true,
		// 	value: this.props.authContext.userPayload.profile.employeeId,
		// },
	};
};

class UpdateProfile extends Component<IAuthProps, UpdateProfileState> {
	state = {
		form: null,
		sendingPasswordReset: false,
		sendingEmailVerification: false,
	};

	componentDidMount() {
		this.init();
	}

	init() {
		const formFields: { [key: string]: DynamicFormField } = getBaseProfileFields(
			true,
			this.props.authContext.userPayload.profile
		);
		formFields.password = {
			...formFields.password,
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				return (
					form.fields.primary_email.touched &&
					form.fields.primary_email.value &&
					form.fields.primary_email.value !== this.props.authContext.userPayload.profile.primary_email
				);
			},
		};
		this.setState({
			form: new DynamicFormValidation(formFields),
		});
	}

	resetPassword() {
		this.setState({ sendingPasswordReset: true });
		this.props.authContext
			.getAuthApp()
			.auth()
			.sendPasswordResetEmail(this.props.authContext.userPayload.email)
			.then((res: any) => {
				NotificationsService.success(
					"Please check for your reset email from Firewire App and follow the instructions.",
					undefined,
					{ autoHideDuration: 9999 }
				);
			})
			.finally(() => {
				this.setState({ sendingPasswordReset: false });
			});
	}

	onSubmit(): Promise<any> {
		console.log("UpdateProfile.onSubmit() form:", this.state.form);
		const form: FormValidation = this.state.form;
		let hasChanges: boolean = false;
		Object.values(form.fields).forEach((f: DynamicFormField) => {
			if (f.touched && f.value !== this.props.authContext.userPayload.profile[f.key]) hasChanges = true;
		});
		if (!hasChanges) {
			NotificationsService.info("No changes to save.");
			return Promise.resolve();
		}
		if (!form.validate()) {
			NotificationsService.error("Fix form errors");
			return Promise.reject();
		}
		const userProfileData = this.state.form.values;
		return this.props.authContext
			.updateUserProfile(userProfileData, this.state.form.fields.password.value)
			.then(() => {
				NotificationsService.success("Profile updated successfully.");
			})
			.catch((err) => {
				if (err.code && err.code === "auth/requires-recent-login") {
					// shouldn't happen, requests password
					NotificationsService.error("Please reauthenticated with your password to update your username.");
					return;
				}
				console.error(err);
				NotificationsService.error("Something went wrong. Please try again later.");
			});
	}

	render() {
		return (
			<div className="d-flex flex-column flex-sm-row align-items-start justify-content-center update-profile-container section-padding">
				<div
					className="d-flex align-items-center mr-5 mb-5"
					key={this.props.authContext.userPayload.displayName}
				>
					<span className="align-self-start">
						<UserImageIcon userPayload={this.props.authContext.userPayload} />
					</span>
					<div>
						<div>
							{this.props.authContext.userPayload.displayName}
							{!this.props.authContext.userPayload.user.emailVerified && (
								<UIStyledTooltip
									title={
										this.state.sendingEmailVerification
											? "Sending email verification"
											: "Click to send email verification"
									}
								>
									<span
										className={clsx(
											"link text-md font-weight-bold text-danger ml-3",
											this.state.sendingEmailVerification && "loading-text"
										)}
										onClick={() => {
											this.setState({ sendingEmailVerification: true });
											this.props.authContext.userPayload.user
												.sendEmailVerification()
												.finally(() => {
													this.setState({ sendingEmailVerification: false });
												})
												.then(() => {
													NotificationsService.success(
														`Email verification sent to ${this.props.authContext.userPayload.user.email}. Follow the link in your email and refresh this page when you are done.`,
														undefined,
														{ autoHideDuration: 99999 }
													);
												})
												.catch((err) => {
													console.error(err);
													NotificationsService.error(
														"Something went wrong. Please try again later."
													);
												});
										}}
									>
										<i className="im im-warning mr-1"></i>Verify Email
									</span>
								</UIStyledTooltip>
							)}
						</div>
						<div>{this.props.authContext.userPayload.email}</div>
						<div className="text-md">
							{formatPhoneNumber(this.props.authContext.userPayload.profile.mobile_number)}
						</div>
						<div>
							<span
								className={clsx(
									"link text-md text-gray",
									this.state.sendingPasswordReset && "loading-text"
								)}
								onClick={() => this.resetPassword()}
							>
								Reset Password
							</span>
						</div>
						<MembershipDetails />
					</div>
				</div>
				<DynamicForm
					form={this.state.form}
					submitBtn="Update"
					submitBtnClassName="px-5"
					resetForm={() => this.init()}
					onSubmit={() => this.onSubmit()}
				></DynamicForm>
			</div>
		);
	}
}

export default withAuth(UpdateProfile, true);
